/**
 * Renders a coarse "time since" label for conversation rows and list metadata.
 * Coarse on purpose: these are glanceable recency cues, not precise durations.
 * A non-parseable or future timestamp falls back to "just now" so a row never
 * shows `NaN`.
 *
 * Formatting goes through `Intl.RelativeTimeFormat` (the default short style keeps the
 * compact "5 min. ago" register) so labels follow the viewer's locale and long
 * gaps climb the unit ladder — weeks, months, years — instead of degrading
 * into "377d ago".
 *
 * It stays in the browser-shared layer so the React UI and compiled tests use
 * the same pure function.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Descending unit ladder; the first threshold the elapsed time clears wins.
// Calendar-agnostic month/year approximations are fine at this coarseness.
const UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: "year", ms: 365 * DAY_MS },
  { unit: "month", ms: 30 * DAY_MS },
  { unit: "week", ms: 7 * DAY_MS },
  { unit: "day", ms: DAY_MS },
  { unit: "hour", ms: HOUR_MS },
  { unit: "minute", ms: MINUTE_MS },
];

// Formatter construction parses locale data — too heavy to repeat per list
// row. One cached instance per requested locale (callers use at most a few).
const formatters = new Map<string, Intl.RelativeTimeFormat>();

function formatterFor(locale: string, style: "short" | "long"): Intl.RelativeTimeFormat {
  const key = `${locale}:${style}`;
  let rtf = formatters.get(key);
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always", style });
    formatters.set(key, rtf);
  }
  return rtf;
}

export function formatRelativeTime(
  value: string | number,
  now: number = Date.now(),
  locale: string = "en",
  style: "short" | "long" = "short",
): string {
  const then = typeof value === "number" ? value : new Date(value).getTime();
  if (Number.isNaN(then)) return "just now";

  const diffMs = now - then;
  if (diffMs < MINUTE_MS) return "just now";

  const rtf = formatterFor(locale, style);
  for (const { unit, ms } of UNITS) {
    if (diffMs >= ms) return rtf.format(-Math.floor(diffMs / ms), unit);
  }
  return "just now";
}
